// Главный экран — порт home_screen из profile-prototype.html (ui-spec.md §2).
// Mock-данные пока в самом файле; в следующих этапах перенесём в zustand-store
// + подтянем с сервера. Структура и стили один-в-один с прототипом.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTonConnectUI, useTonAddress } from '@tonconnect/ui-react';
import { useTelegram } from '../telegram/useTelegram';
import { UserAvatar } from '../components/UserAvatar';
import { ScriptsIcon } from '../components/ScriptsIcon';
import { PlayerName } from '../components/PlayerName';
import { DailyLoginWidget } from '../components/DailyLoginWidget';
import { MessagesModal } from '../components/MessagesModal';
import { InfoModal } from '../components/InfoModal';
import { WalletPanel } from '../components/WalletPanel';
import { ClawbackScene } from '../components/ClawbackScene';
import { useStudyMode } from '../study/useStudyMode';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import { useGameStore } from '../store/useGameStore';
import { markClawbackSeen } from '../api/client';
import { useTr } from '../i18n/useTr';
import styles from './HomeScreen.module.css';

// История транзакций подгружается с сервера через listTransactions().
// Подгрузка происходит при открытии модалки «БАЛАНС».

const DAILY_DOTS_TOTAL = 7;
const DAILY_NEXT_LABEL = 'завтра: ШК';
const DAILY_REWARD = 10;

// Лента активности других игроков. Будет подключена к серверу позже
// (GET /api/feed/events). Пока пусто.
const EVENTS: { color: string; icon: string; who: string; text: string; what: string }[] = [];

// Лор-новости — fallback тексты для нижней NEWS-ленты, когда сервер
// не отдал ни одной активной новости (или клиент offline). Реальные
// новости приходят с /api/news-ticker/current и заменяют этот массив.
// ФТУЕ.7: возвращаем 2 списка (ru/en), генератор выбирает по языку.
function buildNewsFallback(t: (ru: string, en: string) => string): string[] {
  return [
    t(
      'Деус Системс возмущена обыском полиции на её складах · детали засекреченной машины обнаружены у Синтетикс Синдикат',
      'Deus Systems outraged by police raid on its warehouses · classified machine parts found at Synthetics Syndicate'
    ),
    t(
      'Лив Лайф анонсировала препарат против кибер-психоза · Глобал Секьюрити сомневается в эффективности',
      'Liv Life announced anti-cyberpsychosis drug · Global Sec doubts effectiveness'
    ),
    t(
      'В трущобах Синтетикс Синдикат снова отжимают территорию у уличных банд',
      'In the slums Synthetics Syndicate are pushing street gangs off turf again'
    ),
    t(
      'Тоталиатор закрыт: ставка 12 USDT принесла 84 USDT владельцу аккаунта SmokeWolf',
      'Totalizer closed: 12 USDT bet brought 84 USDT to account SmokeWolf'
    )
  ];
}

// Хук тянет активные лор-новости с сервера. Если сервер не ответил/
// пусто — возвращает fallback. Перезапрос раз в 12 часов (лор-новости
// меняются редко; Этап 62 — экономия Neon, было 60с).
function useLoreNews(fallback: string[]): string[] {
  const [items, setItems] = useState<string[]>(fallback);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const base = ((import.meta as any).env?.VITE_API_URL || '').replace(/\/$/, '');
        const r = await fetch(`${base}/api/news-ticker/current`);
        if (!r.ok) return;
        const data = await r.json();
        if (!alive) return;
        if (Array.isArray(data) && data.length > 0) {
          setItems(data.map((n: any) => n.text));
        }
      } catch { /* offline → оставляем fallback */ }
    };
    load();
    const t = setInterval(load, 12 * 60 * 60 * 1000); // 12 часов
    return () => { alive = false; clearInterval(t); };
  }, []);
  return items;
}

export function HomeScreen() {
  const navigate = useNavigate();
  const { hapticTap, alert, user } = useTelegram();
  const t = useTr();
  const isEn = t('ru', 'en') === 'en';
  const localeFmt = isEn ? 'en-US' : 'ru-RU';

  // Все данные из store. Изменения в store → все экраны перерисуются.
  const player = useGameStore(s => s.player);
  const avatarId = useGameStore(s => s.activeAvatarId);
  const accentColor = useGameStore(s => s.profileAccentColor);
  const activeTitle = useGameStore(s => s.activeTitle);
  // НОВ.1: лор-новости с сервера (или fallback).
  const newsItems = useLoreNews(buildNewsFallback(t));
  const currencies = useGameStore(s => s.currencies);
  const progress = useGameStore(s => s.progress);
  const counts = useGameStore(s => s.counts);
  const setPlayerName = useGameStore(s => s.setPlayerName);
  const claimDaily = useGameStore(s => s.claimDaily);
  const setTonConnected = useGameStore(s => s.setTonConnected);
  // ЗВЁЗДЫ.2 (Этап 76): очередь изъятий карт за Stars-рефанд → рулетка.
  const pendingClawback = useGameStore(s => s.pendingClawback);
  const dismissClawback = useGameStore(s => s.dismissClawback);
  // Модалки.
  const [scriptsHistOpen, setScriptsHistOpen] = useState(false);
  const [usdtHistOpen, setUsdtHistOpen] = useState(false);
  const [pvpOpen, setPvpOpen] = useState(false);
  const [loreOpen, setLoreOpen] = useState(false);
  const toggleStudy = useStudyMode(s => s.toggle);
  const studyActive = useStudyMode(s => s.active);
  // TON Connect.
  const [tonConnectUI] = useTonConnectUI();
  const tonAddress = useTonAddress();
  const [messagesOpen, setMessagesOpen] = useState(false);
  // Счётчик непрочитанных = сообщения с сервера + git-коммиты.
  const {
    unread: unreadMessages,
    serverMessages,
    commits,
    hideCommit,
    hideAllCommits,
    markAllRead,
    refresh: refreshMessages
  } = useUnreadMessages();

  // Один раз при монтировании — синхронизируем имя из Telegram с store.
  // Если юзер открыл в Telegram под своим аккаунтом → его first_name
  // станет нашим ником. В web-режиме оставляем дефолт.
  useEffect(() => {
    if (user?.first_name && player.nick === 'Ник_игрока') {
      setPlayerName(user.first_name);
    }
  }, [user?.first_name, player.nick, setPlayerName]);

  // Синхронизация подключения TON-кошелька в store.
  useEffect(() => {
    setTonConnected(!!tonAddress);
  }, [tonAddress, setTonConnected]);

  // Ежедневная награда: списываем 1 dailyDay и +10 Скриптов через store.
  const onDailyClaim = () => {
    hapticTap();
    if (progress.dailyDotsDone >= 7) {
      alert(t('Все 7 точек на этой неделе уже собраны!', 'All 7 dots collected this week!'));
      return;
    }
    claimDaily();
    alert(t(
      `+${DAILY_REWARD} Скриптов. День ${progress.dailyDay + 1} / 7`,
      `+${DAILY_REWARD} Scripts. Day ${progress.dailyDay + 1} / 7`
    ));
  };

  // TON-кошелёк: открыть модалку TonConnect (или отключить если уже подключен).
  const onTonClick = () => {
    hapticTap();
    if (tonAddress) {
      // Уже подключен — показать адрес + предложить отключить.
      const short = tonAddress.slice(0, 4) + '…' + tonAddress.slice(-4);
      const ok = window.confirm(t(
        `Кошелёк: ${short}\n\nОтключить?`,
        `Wallet: ${short}\n\nDisconnect?`
      ));
      if (ok) tonConnectUI.disconnect();
    } else {
      tonConnectUI.openModal();
    }
  };

  const nav = (path: string) => () => {
    hapticTap();
    navigate(path);
  };
  const todo = (label: string) => () => {
    hapticTap();
    alert(t(`${label} — в разработке`, `${label} — coming soon`));
  };

  // ЗВЁЗДЫ.2: показываем рулетку изъятия для первого элемента очереди.
  const clawbackEntry = pendingClawback && pendingClawback.length > 0 ? pendingClawback[0] : null;

  return (
    <div className={styles.screen}>
      {clawbackEntry && clawbackEntry.cards && clawbackEntry.cards.length > 0 && (
        <ClawbackScene
          entry={clawbackEntry}
          onDone={async () => {
            dismissClawback();
            try { await markClawbackSeen(); } catch { /* offline */ }
          }}
        />
      )}
      <div className={styles.content}>
        {/* debug + BP */}
        <div className={styles.debug}>
          <span>[ {t('КОРПЫ', 'CORPS')} ] :: v0.1 React</span>
          <span className={`${styles.bpStatus} ${progress.hasBattlePass ? styles.bpStatusBP : styles.bpStatusF2P}`}>
            {progress.hasBattlePass ? t('БП-АКТИВЕН', 'BP-ACTIVE') : 'F2P'}
          </span>
        </div>

        {/* НОВ.1: серверные лор-новости подключены к нижней ленте «● NEWS»
            (см. NewsItemsFromServer). Отдельная marquee сверху больше не нужна. */}

        {/* Профильная плашка */}
        <button className={styles.profile} onClick={nav('/profile')} data-tt="hm-profile">
          <div className={styles.profAvatar}>
            <UserAvatar
              avatarId={avatarId}
              size={48}
            />
            <div className={styles.profAvatarBadge}>{player.level}</div>
          </div>
          <div className={styles.profInfo}>
            <div className={styles.profName}>
              <PlayerName
                name={player.nick}
                accentColor={accentColor || undefined}
                title={activeTitle}
                size="md"
                inline
              />
            </div>
            <div className={styles.profRole}>{player.role}</div>
            <div className={styles.profStats}>
              <div className={styles.profStat}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#FFB13D" strokeWidth="1.5">
                  <path d="M7 4h10v2a5 5 0 0 1-5 5 5 5 0 0 1-5-5V4z"/>
                  <path d="M9 19h6M12 11v8"/>
                </svg>
                {player.cupProgress.current}
                <span className="sub">/{player.cupProgress.max}</span>
              </div>
              <div className={styles.profStat} style={{ color: 'var(--neon-am)' }}>
                <svg width="11" height="11" viewBox="0 0 24 26" fill="none" stroke="#FFB13D" strokeWidth="1.5">
                  <path d="M12 2l9 3v8c0 6-4 10-9 11-5-1-9-5-9-11V5z" fill="rgba(255,177,61,0.12)"/>
                </svg>
                {player.firewalls}
              </div>
            </div>
          </div>
          <div className={styles.profTopBtns}>
            <div className={styles.profTopBtnWrap} data-tt="hm-msg">
              <button
                className={styles.profTopBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  hapticTap();
                  refreshMessages();   // подтянуть свежие сообщения с сервера прямо сейчас
                  setMessagesOpen(true);
                  markAllRead();
                }}
                aria-label={t('Сообщения', 'Messages')}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#A0A4B4" strokeWidth="1.5">
                  <rect x="3" y="6" width="18" height="13" rx="1"/>
                  <path d="M3 8l9 6 9-6"/>
                </svg>
              </button>
              {/* Бейдж вне clip-path: рендерится поверх обёртки, не режется.
                  Счётчик из localStorage — реальные новые коммиты после
                  последнего открытия модалки. */}
              {unreadMessages > 0 && (
                <span className={styles.profTopBadge}>{unreadMessages}</span>
              )}
            </div>
            <div className={styles.profTopBtnWrap}>
              <button
                className={styles.profTopBtn}
                onClick={(e) => { e.stopPropagation(); hapticTap(); toggleStudy(); }}
                aria-label={t('Режим изучения', 'Study mode')}
                data-tt-exit
                style={{
                  fontSize: 17,
                  background: studyActive ? 'var(--neon-cy)' : undefined,
                  color: studyActive ? 'var(--bg)' : undefined,
                  zIndex: studyActive ? 103 : undefined
                }}
              >
                ?
              </button>
            </div>
          </div>
        </button>

        {/* Модалка «Сообщения» — admin/auto messages + git changelog */}
        {messagesOpen && (
          <MessagesModal
            onClose={() => setMessagesOpen(false)}
            serverMessages={serverMessages}
            commits={commits}
            onHideCommit={hideCommit}
            onHideAllCommits={hideAllCommits}
            onMessagesChanged={refreshMessages}
          />
        )}

        {/* Валюты */}
        <div className={styles.curRow}>
          <button className={styles.cur} onClick={() => { hapticTap(); setScriptsHistOpen(true); }} data-tt="hm-scripts">
            <ScriptsIcon size={16} />
            <div className={styles.curCol}>
              <span className={styles.curValue}>{currencies.scripts.toLocaleString(localeFmt)}</span>
              <span className={styles.curLabel}>{t('скрипты', 'scripts')}</span>
            </div>
          </button>
          <button className={styles.cur} onClick={() => { hapticTap(); setUsdtHistOpen(true); }} data-tt="hm-usdt">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="#26A17B" stroke="none">
              <circle cx="12" cy="12" r="11"/>
              <text x="12" y="16" fontFamily="Inter" fontSize="10" fontWeight="700" fill="#fff" textAnchor="middle">$</text>
            </svg>
            <div className={styles.curCol}>
              <span className={styles.curValue}>{currencies.usdt.toFixed(2)}</span>
              <span className={styles.curLabel}>USDT</span>
            </div>
          </button>
          <button className={styles.cur} onClick={onTonClick} data-tt="hm-ton">
            <svg width="13" height="13" viewBox="0 0 56 56" fill="none">
              <path d="M28 6L6 18l22 32L50 18z" fill="#3DF0FF" fillOpacity="0.15" stroke="#3DF0FF" strokeWidth="2.5" strokeLinejoin="round"/>
            </svg>
            <div className={styles.curCol}>
              <span className="cd" style={{ color: 'var(--neon-cy)', fontSize: 11, letterSpacing: '0.1em' }}>TON</span>
              <div className={styles.tonConnected}>
                {currencies.tonConnected ? (
                  <>
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#9CFF3D" strokeWidth="3">
                      <path d="M5 13l5 5L20 7"/>
                    </svg>
                    <span>{t('подключен', 'connected')}</span>
                  </>
                ) : (
                  <span style={{ color: 'var(--t-lo)' }}>{t('подключить', 'connect')}</span>
                )}
              </div>
            </div>
          </button>
        </div>

        {/* Бегущая лента */}
        <div className={styles.marquee}>
          <div className={styles.marqueePin}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3DF0FF" strokeWidth="1.8">
              <circle cx="6" cy="12" r="2" fill="#3DF0FF"/>
              <path d="M11 8a4 4 0 0 1 0 8M14 5a7 7 0 0 1 0 14"/>
            </svg>
          </div>
          <div className={styles.marqueeFlow}>
            <div className={styles.marqueeTrack}>
              {/* Дублируем events дважды, чтобы анимация была бесшовной */}
              {[...EVENTS, ...EVENTS].map((e, i) => (
                <div key={i} className={styles.marqueeItem}>
                  <span style={{ color: e.color }}>{e.icon}</span>{' '}
                  <span style={{ color: 'var(--t-hi)' }}>{e.who}</span>{' '}
                  {e.text}{' '}
                  {e.what && <span style={{ color: e.color }}>{e.what}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* PVP */}
        <button className={styles.btnBattle} onClick={() => { hapticTap(); navigate('/pvp'); }} data-tt="hm-pvp">
          <div>
            <div className={styles.btnBattleLbl}>PVP</div>
            <div className={styles.btnBattleSub}>{t('Бои против игроков · ставки в USDT', 'Battles vs players · USDT bets')}</div>
          </div>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#3DF0FF" strokeWidth="1.5">
            <path d="M4 7l8-4 8 4v5c0 5-4 8-8 9-4-1-8-4-8-9z"/>
          </svg>
        </button>

        {/* PVE — кампания + туториал. Будут переписаны. */}
        <button
          className={`${styles.btnBattle} ${styles.btnBattlePve}`}
          onClick={todo(t('PvE / Кампания', 'PvE / Campaign'))}
          data-tt="hm-pve"
        >
          <div>
            <div className={styles.btnBattleLbl}>PVE</div>
            <div className={styles.btnBattleSub}>{t('Кампания · скоро', 'Campaign · soon')}</div>
          </div>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#A0A4B4" strokeWidth="1.5">
            <path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z"/>
          </svg>
        </button>

        {/* МАР: внутриигровой маркетплейс карт (Этап 56). */}
        <button
          className={styles.btnBattle}
          onClick={nav('/marketplace')}
          data-tt="hm-market"
        >
          <div>
            <div className={styles.btnBattleLbl}>{t('МАРКЕТ', 'MARKET')}</div>
            <div className={styles.btnBattleSub}>{t('Карты от игроков · Скр / USDT · аукционы', 'Cards from players · Scr / USDT · auctions')}</div>
          </div>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#FF3D8F" strokeWidth="1.5">
            <path d="M3 7h18l-2 11H5L3 7z"/>
            <path d="M8 7V5a4 4 0 0 1 8 0v2"/>
            <circle cx="9" cy="11" r="0.8" fill="#FF3D8F"/>
            <circle cx="15" cy="11" r="0.8" fill="#FF3D8F"/>
          </svg>
        </button>

        {/* Тотализатор + Рейтинг */}
        <div className={styles.totRow}>
          <div className={styles.totBox} data-tt="hm-tot">
            <div className={styles.totHead}>
              <span className={`${styles.totLbl} ${styles.totLblTot}`}>{t('ТОТАЛИЗАТОР', 'TOTALIZER')}</span>
            </div>
            <div className={styles.totRow2}>
              <span className={styles.totHint}>{t('зрит. ставки', 'spectator bets')}</span>
              <span className={styles.totSoon}>SOON</span>
            </div>
          </div>
          <button className={styles.totBox} onClick={nav('/rating')} style={{ textAlign: 'left' }} data-tt="hm-rating">
            <div className={styles.totHead}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#FFB13D" strokeWidth="1.5">
                <path d="M12 3l8 4v6c0 4-3 7-8 8-5-1-8-4-8-8V7z"/>
              </svg>
              <span className={`${styles.totLbl} ${styles.totLblRating}`}>{t('РЕЙТИНГ', 'RATING')}</span>
            </div>
            <div className={styles.totRow2}>
              <span className={styles.totHint}>{t('место', 'place')}</span>
              <span className={styles.totPlace}>{progress.ratingPlace}</span>
            </div>
          </button>
        </div>

        {/* === НИЖНЯЯ ГРУППА (прижата к низу через margin-top: auto) === */}
        <div className={styles.bottom}>

        {/* Broadcast — LIVE-новость с диктором */}
        <button className={styles.broadcast} onClick={() => { hapticTap(); setLoreOpen(true); }} data-tt="hm-news">
          <div className={styles.broadcastAnchor}>
            <div className={styles.liveDot}>
              <span className={styles.liveDotPulse} /> LIVE
            </div>
            <svg viewBox="0 0 24 24" fill="none" stroke="#FF3D8F" strokeWidth="1.2">
              <circle cx="12" cy="8" r="4"/>
              <path d="M4 21a8 8 0 0 1 16 0"/>
              <line x1="8" y1="6" x2="16" y2="6" stroke="#3DF0FF" strokeWidth="2" opacity="0.6"/>
            </svg>
            <div className={styles.broadcastCh}>CH-1998</div>
          </div>
          <div className={styles.broadcastText}>
            <div className={styles.broadcastHead}>
              <span><span className={styles.broadcastZone}>ZONE NEWS</span> · 21:47</span>
              <span>ANC-1998</span>
            </div>
            <div className={styles.broadcastHeadline}>
              {isEn ? (
                <>Corp war: <span style={{ color: 'var(--deus)' }}>Deus</span> vs <span style={{ color: 'var(--syn)' }}>Synthetics</span></>
              ) : (
                <>Война корпораций: <span style={{ color: 'var(--deus)' }}>Деус</span> против <span style={{ color: 'var(--syn)' }}>Синтетикс</span></>
              )}
            </div>
            <div className={styles.broadcastSub}>
              {t(
                'Взрыв на складе Деус в 03:14 · детали машины обнаружены у Синтетикс · полиция молчит',
                'Explosion at Deus warehouse 03:14 · machine parts found at Synthetics · police silent'
              )}
            </div>
          </div>
        </button>

        {/* Лор-marquee */}
        <div className={styles.newsMarquee}>
          <div className={styles.newsPin}>
            <span className={styles.newsPinDot} /> NEWS
          </div>
          <div className={styles.marqueeFlow}>
            <div className={styles.newsTrack}>
              {[...newsItems, ...newsItems].map((n, i) => (
                <div key={i} className={styles.newsItem}>{n}</div>
              ))}
            </div>
          </div>
        </div>

        {/* ЕЖВ.1: новый виджет ежедневного входа с серверным state. */}
        <DailyLoginWidget />

        {/* 4-сетка плиток. Каждая плитка — в .grid4TileWrap, чтобы бейджи
            счётчиков не обрезались clip-path плитки. */}
        <div className={styles.grid4}>
          <div className={styles.grid4TileWrap}>
            <button className={styles.grid4Tile} onClick={nav('/shop')} data-tt="hm-shop">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FF3D8F" strokeWidth="1.5">
                <path d="M4 12l4-8h8l4 8-8 10z"/>
                <path d="M8 4l4 8 4-8M4 12h16"/>
              </svg>
              <span className={styles.grid4Lbl}>{t('МАГАЗИН', 'SHOP')}</span>
            </button>
          </div>
          <div className={styles.grid4TileWrap}>
            <button className={styles.grid4Tile} onClick={nav('/quests')} data-tt="hm-quests">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#9CFF3D" strokeWidth="1.5">
                <path d="M6 2h9l5 5v13a2 2 0 0 1-2 2h-12a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/>
                <path d="M14 2v6h6"/>
              </svg>
              <span className={styles.grid4Lbl}>{t('ЗАДАНИЯ', 'QUESTS')}</span>
            </button>
            {counts.quests > 0 && (
              <span className={styles.grid4Badge}>{counts.quests}</span>
            )}
          </div>
          <div className={styles.grid4TileWrap}>
            <button className={styles.grid4Tile} onClick={nav('/bp')} data-tt="hm-bp">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFB13D" strokeWidth="1.5">
                <path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z"/>
              </svg>
              <span className={styles.grid4Lbl}>{isEn ? <>BATTLE<br />PASS</> : <>БОЕВОЙ<br />ПРОПУСК</>}</span>
            </button>
            <span className={`${styles.grid4Badge} ${styles.grid4BadgeAmber}`}>{progress.bpLevel}</span>
          </div>
          <div className={styles.grid4TileWrap}>
            <button className={styles.grid4Tile} onClick={nav('/decks')} data-tt="hm-deck">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3DF0FF" strokeWidth="1.5">
                <rect x="3" y="5" width="7" height="14" rx="1"/>
                <rect x="12" y="5" width="7" height="14" rx="1"/>
              </svg>
              <span className={styles.grid4Lbl}>{t('КОЛОДА', 'DECK')}</span>
            </button>
          </div>
        </div>

        </div> {/* /.bottom */}
      </div>

      {/* === Модалки балансов (вторичная справка, открывается из бейджей валют) === */}
      {scriptsHistOpen && (
        <InfoModal title={t('БАЛАНС СКРИПТОВ', 'SCRIPTS BALANCE')} onClose={() => setScriptsHistOpen(false)} size="tall">
          <div style={{ textAlign: 'center', padding: '12px 0 16px' }}>
            <div style={{ color: 'var(--neon-li)', fontSize: 32, fontWeight: 700 }}>
              {currencies.scripts.toLocaleString(localeFmt)}
            </div>
            <div style={{ fontSize: 11, color: 'var(--t-lo)', letterSpacing: 1 }}>{t('Скриптов на счету', 'Scripts in account')}</div>
          </div>
          <h4>{t('История операций', 'Transaction history')}</h4>
          <TransactionsList kind="scripts" />
        </InfoModal>
      )}

      {usdtHistOpen && (
        <InfoModal title={t('USDT КОШЕЛЁК', 'USDT WALLET')} onClose={() => setUsdtHistOpen(false)} size="tall">
          <WalletPanel tonAddress={tonAddress} />
        </InfoModal>
      )}

      {pvpOpen && (
        <InfoModal title={t('PvP-БОИ', 'PvP BATTLES')} onClose={() => setPvpOpen(false)}>
          <p>{t(
            'Реальные противники, ставка от 1 USDT, победитель забирает банк × 2.',
            'Real opponents, bet from 1 USDT, winner takes the pot × 2.'
          )}</p>
          <h4>{t('Запускается в Этапе 46', 'Launching in Stage 46')}</h4>
          <p>{t(
            'Нужен real-time сервер (Colyseus + Redis) для синхронизации хода. Пока недоступно.',
            'Needs a real-time server (Colyseus + Redis) for turn sync. Not available yet.'
          )}</p>
          <p>{t(
            'Сейчас сыграй PvE-туториал — он отрабатывает движок и подготовит к PvP.',
            'For now play the PvE tutorial — it warms up the engine and gets you ready for PvP.'
          )}</p>
        </InfoModal>
      )}

      {loreOpen && (
        <InfoModal title={t('НОВОСТИ УЛИЦЫ', 'STREET NEWS')} onClose={() => setLoreOpen(false)}>
          {newsItems.map((n, i) => (
            <p key={i} style={{ paddingBottom: 8, borderBottom: '0.5px solid var(--line)' }}>· {n}</p>
          ))}
          <p style={{ marginTop: 12, fontStyle: 'italic', color: 'var(--t-lo)' }}>
            {t(
              'Новости меняются каждый сезон. Источник — лор-DLC от создателей.',
              'News rotate every season. Source — lore DLC from the devs.'
            )}
          </p>
        </InfoModal>
      )}
    </div>
  );
}

// === Компонент истории транзакций ===
// Подгружает с сервера при монтировании. Используется в модалках балансов.
function TransactionsList({ kind }: { kind: 'scripts' | 'usdt' }) {
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  const localeFmt = isEn ? 'en-US' : 'ru-RU';
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<import('../api/client').TransactionDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { listTransactions } = await import('../api/client');
        const list = await listTransactions(kind, 50);
        if (!cancelled) setItems(list);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || tr('Не удалось загрузить', 'Failed to load'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kind, tr]);

  if (loading) return <div style={{ color: 'var(--t-lo)', textAlign: 'center', padding: 16 }}>{tr('Загрузка…', 'Loading…')}</div>;
  if (error)   return <div style={{ color: '#ff7a7a', textAlign: 'center', padding: 16 }}>{tr('Ошибка', 'Error')}: {error}</div>;
  if (items.length === 0) {
    return (
      <div style={{ color: 'var(--t-lo)', textAlign: 'center', padding: 16, fontStyle: 'italic', fontSize: 12 }}>
        {isEn ? (
          <>No transactions yet.<br />The first one will appear after a reward or purchase.</>
        ) : (
          <>Операций пока нет.<br />Первая транзакция появится после получения награды или покупки.</>
        )}
      </div>
    );
  }

  const fmtDelta = (kind: string, delta: number) => {
    if (kind === 'usdt') return `${delta > 0 ? '+' : ''}$${(delta / 100).toFixed(2)}`;
    return `${delta > 0 ? '+' : ''}${delta}`;
  };

  const reasonLabel = (r: string): string => {
    if (r === 'quest_reward')  return tr('Награда за задание', 'Quest reward');
    if (r === 'admin_grant')   return tr('Подарок от админа', 'Admin gift');
    if (r === 'shop_pack')     return tr('Покупка ШК', 'Cipher Crate purchase');
    if (r === 'shop_hero')     return tr('Покупка Героя', 'Hero purchase');
    if (r === 'shop_firewall') return tr('Покупка Файервола', 'Firewall purchase');
    if (r === 'shop_bp')       return tr('Покупка БП', 'Battle Pass purchase');
    if (r === 'daily_login')   return tr('Ежедневный вход', 'Daily login');
    if (r.startsWith('match_won'))   return tr('Победа в матче', 'Match win');
    if (r.startsWith('match_lost'))  return tr('Поражение в матче', 'Match loss');
    // ВОЙ.2: метки транзакций Войны Корпораций.
    if (r === 'war_hp:win')         return tr('Война: победа (HP)', 'War: win (HP)');
    if (r === 'war_hp:loss')        return tr('Война: поражение (HP)', 'War: loss (HP)');
    if (r === 'war_retreat:win')    return tr('Война: победа (отступление)', 'War: win (retreat)');
    if (r === 'war_retreat:loss')   return tr('Война: отступление', 'War: retreated');
    if (r === 'war_abandon:win')    return tr('Война: победа (соперник вышел)', 'War: win (opp left)');
    if (r === 'war_abandon:loss')   return tr('Война: поражение (выход)', 'War: loss (left)');
    return r;
  };

  return (
    <div>
      {items.map(t => (
        <div key={t.id} style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '6px 0',
          borderBottom: '0.5px solid var(--line)',
          fontSize: 13
        }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--t-lo)' }}>
              {new Date(t.createdAt).toLocaleString(localeFmt, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </div>
            <div>{reasonLabel(t.reason)}{t.meta?.title ? ` · «${t.meta.title}»` : ''}</div>
          </div>
          <div style={{ fontWeight: 700, color: t.delta > 0 ? 'var(--neon-li)' : '#ff7a7a' }}>
            {fmtDelta(t.kind, t.delta)}
          </div>
        </div>
      ))}
    </div>
  );
}
